import React from 'react';
import ManagedLayout from '../ManagedLayout';
import './LayoutManagerGrid.scss';
import { Icon, IconNames } from '@instinet-analytics/semantic-ui-components';
import Tooltip from 'utils/components/Tooltip/Tooltip';

import sortBy from 'lodash/sortBy';
import classNames from 'classnames';
import { LayoutManagerDictionary } from 'layoutManagement/LayoutManagerDictionary';

interface LayoutManagerGridProps {
    dictionary: LayoutManagerDictionary;
    shareLayoutFeatureToggleEnabled: boolean;
    rowData: { isActive: boolean; layout: ManagedLayout }[];
    loadIcon?: IconNames;
    isAppLayoutManager: boolean;
    loadHoverIcon?: IconNames;
    onLoadClick: (layout: ManagedLayout) => void;
    onShareClick: (layout: ManagedLayout) => void;
    onDuplicateClick: (layout: ManagedLayout) => void;
    onDeleteClick: (layout: ManagedLayout) => void;
}

interface LayoutManagerRowProps {
    isActive: boolean;
    dictionary: LayoutManagerDictionary;
    layout: ManagedLayout;
    isAppLayoutManager: boolean;
    gridProps: LayoutManagerGridProps;
    loadIcon: IconNames;
    loadHoverIcon: IconNames;
}

export default function LayoutManagerGrid(props: LayoutManagerGridProps) {
    const {
        dictionary,
        shareLayoutFeatureToggleEnabled,
        rowData,
        isAppLayoutManager,
        loadIcon: loadIconProps,
        loadHoverIcon: loadHoverIconProps,
    } = props;

    const loadIcon = loadIconProps ?? 'circle outline';
    const loadHoverIcon = loadHoverIconProps ?? 'check circle outline';

    return (
        <div className="LayoutManagerGrid">
            <div className="simple-grid">
                {renderHeaders(shareLayoutFeatureToggleEnabled)}
                {sortBy(rowData, 'name').map((r) => (
                    <div
                        className={classNames(
                            'grid-row',
                            r.isActive && 'active-layout'
                        )}
                        data-layout-id={r.layout.layoutId}
                        key={`${r.layout.layoutId}-${r.layout.name}`}
                    >
                        <LayoutManagerRow
                            dictionary={dictionary}
                            layout={r.layout}
                            isActive={r.isActive}
                            isAppLayoutManager={isAppLayoutManager}
                            gridProps={props}
                            loadIcon={loadIcon}
                            loadHoverIcon={loadHoverIcon}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
}

function LayoutManagerRow(props: LayoutManagerRowProps) {
    const { isActive, layout } = props;

    const {
        shareLayoutFeatureToggleEnabled,
        isAppLayoutManager,
        onLoadClick,
        onShareClick,
        onDuplicateClick,
        onDeleteClick,
    } = props.gridProps;

    const sharedWith =
        layout.sharedWith && layout.sharedWith.length > 0
            ? layout.sharedWith.join(', ')
            : 'Not shared';

    const onClickRow = () => {
        if (isAppLayoutManager) {
            // Dropdown is now used to load layouts
            return;
        }
        onLoadClick(layout);
    };

    return (
        <>
            <div className="grid-col layout-name" onClick={onClickRow}>
                <Tooltip showDelay={0} text={layout.name} neverHide={true}>
                    <span>{layout.name}</span>
                    {isActive && (
                        <div className="active-layout-indicator">(Active)</div>
                    )}
                    {layout.isGlobal && <span>&nbsp;(Global)</span>}
                </Tooltip>
            </div>
            {shareLayoutFeatureToggleEnabled && (
                <>
                    <div
                        className="grid-col layout-sharedWith"
                        onClick={onClickRow}
                    >
                        <span title={sharedWith}>{sharedWith}</span>
                    </div>
                </>
            )}
            <div className="grid-col col-actions">
                <div>
                    <Icon
                        name="clone outline"
                        className="copy-button"
                        title={`Duplicate ${layout.name}`}
                        onClick={() => onDuplicateClick(layout)}
                    />
                    {!layout.isGlobal && shareLayoutFeatureToggleEnabled && (
                        <Icon
                            name="share alternate"
                            className="share-button"
                            title={`Share ${layout.name}`}
                            onClick={() => onShareClick(layout)}
                        />
                    )}
                    {!layout.isGlobal && (
                        <Icon
                            name="trash"
                            className="delete-button"
                            title={`Delete ${layout.name}`}
                            onClick={() => onDeleteClick(layout)}
                        />
                    )}
                </div>
            </div>
        </>
    );
}

function renderHeaders(shareLayoutFeatureToggleEnabled: boolean) {
    return (
        <div className="grid-row grid-row-header">
            <div className="layout-name">Name</div>
            {shareLayoutFeatureToggleEnabled && (
                <div className="layout-sharedWith">Shared With</div>
            )}
            <div className="col-actions">
                <div>Actions</div>
            </div>
        </div>
    );
}
